// Modal de Limite de Reposição (Suprimentos) — atende `createLimit` e
// `updateLimit` com o MESMO componente; o que separa criação de edição é o
// uuid, não a existência de `selectedObject` (o shell entrega `{}` ao criar, e
// `{}` é verdadeiro).
//
// O Limite é a regra que decide quando pedir, quanto pedir, por quanto e com
// que urgência. As contas e a leitura do banco moram em ./limite/tipos.
import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import * as Casca from "../../../styles/components/configuration/modals/_casca";
import * as Estilo from "../../../styles/components/configuration/modals/LimitModal";
import {
  DIAS_DA_SEMANA,
  FormularioDeLimite,
  LimitModalProps,
  POLITICAS_DE_LEMBRETE,
  TIPOS_DE_ALERTA,
  acharEmbalagemAcordada,
  calcularSugestao,
  formatarQuantidade,
  formularioVazio,
  lerLimite,
  numeroOuNulo,
} from "./limite/tipos";
import { useModalAcessivel } from "../../../hooks/useModalAcessivel";

const LimitModal: React.FC<LimitModalProps> = ({
  selectedObject,
  handleCloseModal,
  handleSave,
  parseInfoValue,
  decryptedInfoToken,
}) => {
  // Esc fecha, o foco fica preso dentro e volta para quem abriu, e o leitor
  // de tela anuncia a janela pelo titulo. Ver hooks/useModalAcessivel.ts.
  const { refDoModal, propsDoDialogo } = useModalAcessivel({
    aoFechar: handleCloseModal,
    idDoTitulo: "titulo-limitmodal",
  });

  const [form, setForm] = useState<FormularioDeLimite>(formularioVazio());
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{
    tom: "ok" | "erro" | "atencao";
    texto: string;
  } | null>(null);

  /**
   * Edição ou criação. `limiteUuid` é o nome do payload; `limitUUID` é o que a
   * linha da tabela traz (DynamicTable case 5) e `replenishmentLimitInfoUuid` o
   * da linha crua do token. Testar só um faria a edição salvar um limite novo
   * ao lado do que já existe, e a loja passaria a ter dois limites para o mesmo
   * SKU.
   */
  const limiteUuid: string | undefined =
    selectedObject?.limiteUuid ??
    selectedObject?.limitUUID ??
    selectedObject?.replenishmentLimitInfoUuid;

  useEffect(() => {
    setForm(lerLimite(selectedObject, parseInfoValue, decryptedInfoToken));
    setAviso(null);
    // `parseInfoValue` vem do shell e é recriada a cada render dele; incluí-la
    // reexecutaria a hidratação por cima do que o usuário está digitando.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedObject]);

  // Travessia organization -> companies -> subsidiaries, o mesmo caminho de
  // Items.tsx e MeiosPagamento.tsx. Não há `GET /subsidiaries` no backend; o
  // token é a fonte.
  const lojas = useMemo(() => {
    const empresas = decryptedInfoToken?.organization?.companies ?? [];
    return empresas.flatMap((empresa: any) =>
      (empresa?.subsidiaries ?? []).map((filial: any) => ({
        uuid: filial?.subsidiaryUuid,
        nome: filial?.subsidiaryName || "(loja sem nome)",
        empresa: empresa?.companyName || "",
      }))
    );
  }, [decryptedInfoToken]);

  /** SKUs = `organizationInfo` do tipo 1; as outras linhas não são produto. */
  const skus = useMemo(() => {
    const infos = decryptedInfoToken?.organization?.organizationInfo ?? [];
    return infos
      .filter((info: any) => Number(info?.organizationInfoType) === 1)
      .map((info: any) => ({
        uuid: info.organizationInfoUuid,
        codigo: info.organizationInfoName || "(sem código)",
        nome: info.organizationInfoDescription || "",
      }));
  }, [decryptedInfoToken]);

  const fornecedores = useMemo(() => {
    const infos = decryptedInfoToken?.supplierInfo ?? [];
    return infos.map((info: any) => ({
      uuid: info.supplierInfoUuid,
      nome: info.supplierInfoDescription || "(fornecedor sem nome)",
    }));
  }, [decryptedInfoToken]);

  /**
   * Locais de estoque: NÃO existe fonte. A aba "Locais de Estoque" nasce com a
   * Fase 4 e tem tabela própria; até lá a lista fica vazia de propósito, com a
   * explicação ao lado. Preencher com a loja repetida faria o campo parecer
   * cadastrado.
   */
  const locaisDeEstoque: { uuid: string; nome: string }[] = [];

  const alterar = (campo: keyof FormularioDeLimite, valor: any) => {
    setForm((atual) => ({ ...atual, [campo]: valor }));
  };

  const alternarDiaDeDisparo = (dia: string) => {
    const atual = form.janelaDisparoDias;
    alterar(
      "janelaDisparoDias",
      atual.indexOf(dia) >= 0
        ? atual.filter((item) => item !== dia)
        : atual.concat(dia)
    );
  };

  // A embalagem vem do preço acordado do fornecedor escolhido, não de um campo
  // aqui: repetir o número criaria dois donos para o mesmo fato.
  const embalagem = useMemo(
    () =>
      acharEmbalagemAcordada(
        decryptedInfoToken,
        form.fornecedorUuid,
        form.skuUuid,
        parseInfoValue
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [decryptedInfoToken, form.fornecedorUuid, form.skuUuid]
  );

  const sugestao = useMemo(
    () => calcularSugestao(form.estoqueMaximo, embalagem),
    [form.estoqueMaximo, embalagem]
  );

  const montarPayload = () => ({
    limiteUuid,
    lojaUuid: form.lojaUuid,
    localDeEstoqueUuid: form.localDeEstoqueUuid || null,
    skuUuid: form.skuUuid,

    estoqueMinimo: numeroOuNulo(form.estoqueMinimo),
    estoqueMaximo: numeroOuNulo(form.estoqueMaximo),
    pontoReposicao: numeroOuNulo(form.pontoReposicao),
    estoqueSeguranca: numeroOuNulo(form.estoqueSeguranca),

    quantidadeModo: form.quantidadeModo,
    // Só o campo do modo escolhido viaja. Mandar os dois deixaria o servidor
    // decidir qual vale — e a decisão é de quem preencheu.
    quantidadeFixa:
      form.quantidadeModo === "fixa" ? numeroOuNulo(form.quantidadeFixa) : null,

    precoModo: form.precoModo,
    precoFixo:
      form.precoModo === "fixoTravado" ? numeroOuNulo(form.precoFixo) : null,

    modo: form.modo,
    tetoDeLinhaReais: numeroOuNulo(form.tetoDeLinhaReais),
    janelaDisparoInicio: form.janelaDisparoInicio,
    janelaDisparoFim: form.janelaDisparoFim,
    janelaDisparoDias: form.janelaDisparoDias,
    fornecedorUuid: form.fornecedorUuid || null,
    fornecedorAlternativoUuid: form.fornecedorAlternativoUuid || null,
    prazoParaEscalarHoras: numeroOuNulo(form.prazoParaEscalarHoras),
    prazoRespostaHoras: numeroOuNulo(form.prazoRespostaHoras),
    politicaLembrete: form.politicaLembrete,

    coberturaAlvoDias: numeroOuNulo(form.coberturaAlvoDias),
    coberturaAlvoOrigem: form.coberturaAlvoOrigem,

    critico: form.critico,
    rearmeHoras: numeroOuNulo(form.rearmeHoras),
    rearmePercentualAbaixo: numeroOuNulo(form.rearmePercentualAbaixo),

    alertType: form.alertType,
  });

  const handleSubmit = async () => {
    if (!form.lojaUuid) {
      setAviso({ tom: "erro", texto: "Escolha a loja deste limite." });
      return;
    }
    if (!form.skuUuid) {
      setAviso({ tom: "erro", texto: "Escolha o SKU deste limite." });
      return;
    }

    const minimo = numeroOuNulo(form.estoqueMinimo);
    const maximo = numeroOuNulo(form.estoqueMaximo);
    if (minimo !== null && maximo !== null && maximo < minimo) {
      setAviso({
        tom: "erro",
        texto:
          `Estoque máximo (${form.estoqueMaximo}) é menor que o mínimo ` +
          `(${form.estoqueMinimo}). Nesse par, a reposição pediria para ficar ` +
          "abaixo do mínimo que ela mesma protege.",
      });
      return;
    }

    if (form.quantidadeModo === "fixa" && !form.quantidadeFixa.trim()) {
      setAviso({
        tom: "erro",
        texto: "Quantidade fixa escolhida, mas o valor está vazio.",
      });
      return;
    }
    if (form.quantidadeModo === "ateOMaximo" && maximo === null) {
      setAviso({
        tom: "erro",
        texto:
          "Modo 'até o máximo' escolhido, mas o estoque máximo está vazio — " +
          "não há alvo para completar.",
      });
      return;
    }

    if (form.precoModo === "fixoTravado" && !form.precoFixo.trim()) {
      setAviso({
        tom: "erro",
        texto: "Preço fixo travado escolhido, mas o valor está vazio.",
      });
      return;
    }

    if (
      form.fornecedorAlternativoUuid &&
      form.fornecedorAlternativoUuid === form.fornecedorUuid
    ) {
      setAviso({
        tom: "erro",
        texto:
          "O fornecedor alternativo é o mesmo do principal: escalar para ele " +
          "seria repetir o pedido para quem já não respondeu.",
      });
      return;
    }

    setSalvando(true);
    try {
      const resultado: any = await handleSave(
        limiteUuid ? "updateLimit" : "createLimit",
        montarPayload()
      );
      // Quem fecha em caso de sucesso é o shell. Deu errado, o modal fica
      // aberto com o motivo: modal que some é indistinguível de sucesso.
      if (resultado && !resultado.ok) {
        setAviso({ tom: resultado.tom ?? "erro", texto: resultado.texto });
      }
    } finally {
      setSalvando(false);
    }
  };

  return ReactDOM.createPortal(
    <Casca.ModalOverlay>
      <Casca.ModalContent ref={refDoModal} {...propsDoDialogo} large>
        <Casca.ModalHeader>
          <Casca.ModalTitle id="titulo-limitmodal">
            <Casca.MaterialIcons>inventory</Casca.MaterialIcons>
            {limiteUuid ? "Editar Limite" : "Novo Limite"}
          </Casca.ModalTitle>
          <Casca.ModalCloseButton onClick={handleCloseModal}>
            <Casca.MaterialIcons>close</Casca.MaterialIcons>
          </Casca.ModalCloseButton>
        </Casca.ModalHeader>

        <Casca.ModalBody>
          <Casca.FormContainer>
            <Casca.FormSection>
              <Casca.SectionTitle>Onde</Casca.SectionTitle>
              <Casca.FormGrid>
                <Casca.LabelInputContainer>
                  <Casca.Label>Loja *</Casca.Label>
                  <Casca.Select
                    value={form.lojaUuid}
                    onChange={(e) => alterar("lojaUuid", e.target.value)}
                  >
                    <option value="">Selecione a loja…</option>
                    {lojas.map((loja: any) => (
                      <option key={loja.uuid} value={loja.uuid}>
                        {loja.empresa ? `${loja.empresa} — ` : ""}
                        {loja.nome}
                      </option>
                    ))}
                  </Casca.Select>
                </Casca.LabelInputContainer>

                <Casca.LabelInputContainer>
                  <Casca.Label>Local de estoque</Casca.Label>
                  <Casca.Select
                    value={form.localDeEstoqueUuid}
                    onChange={(e) =>
                      alterar("localDeEstoqueUuid", e.target.value)
                    }
                  >
                    <option value="">
                      {locaisDeEstoque.length === 0
                        ? "Nenhum local cadastrado"
                        : "A loja inteira"}
                    </option>
                    {locaisDeEstoque.map((local) => (
                      <option key={local.uuid} value={local.uuid}>
                        {local.nome}
                      </option>
                    ))}
                  </Casca.Select>
                  <Estilo.ExplicacaoCurta>
                    Opcional. Sem local, o limite vale para a loja inteira.
                  </Estilo.ExplicacaoCurta>
                </Casca.LabelInputContainer>

                <Casca.LabelInputContainer>
                  <Casca.Label>SKU *</Casca.Label>
                  <Casca.Select
                    value={form.skuUuid}
                    onChange={(e) => alterar("skuUuid", e.target.value)}
                  >
                    <option value="">Selecione o SKU…</option>
                    {skus.map((sku: any) => (
                      <option key={sku.uuid} value={sku.uuid}>
                        {sku.codigo}
                        {sku.nome ? ` — ${sku.nome}` : ""}
                      </option>
                    ))}
                  </Casca.Select>
                </Casca.LabelInputContainer>

                {locaisDeEstoque.length === 0 && (
                  <Estilo.NotaDeCampo>
                    A lista de locais está vazia porque ainda não há nenhum
                    cadastrado: quem cadastra é a aba{" "}
                    <strong>Locais de Estoque</strong>, ao lado desta em
                    Suprimentos.
                  </Estilo.NotaDeCampo>
                )}
              </Casca.FormGrid>
            </Casca.FormSection>

            <Casca.FormSection>
              <Casca.SectionTitle>Quantidades</Casca.SectionTitle>
              {/* Quantidade é Decimal(18,6) no banco: seis casas em todos. */}
              <Casca.FormGrid>
                <Casca.LabelInputContainer>
                  <Casca.Label>Estoque mínimo</Casca.Label>
                  <Casca.Input
                    type="number"
                    min="0"
                    step="0.000001"
                    value={form.estoqueMinimo}
                    onChange={(e) => alterar("estoqueMinimo", e.target.value)}
                    placeholder="0,000000"
                  />
                </Casca.LabelInputContainer>

                <Casca.LabelInputContainer>
                  <Casca.Label>Estoque máximo</Casca.Label>
                  <Casca.Input
                    type="number"
                    min="0"
                    step="0.000001"
                    value={form.estoqueMaximo}
                    onChange={(e) => alterar("estoqueMaximo", e.target.value)}
                    placeholder="0,000000"
                  />
                </Casca.LabelInputContainer>

                <Casca.LabelInputContainer>
                  <Casca.Label>Ponto de reposição</Casca.Label>
                  <Casca.Input
                    type="number"
                    min="0"
                    step="0.000001"
                    value={form.pontoReposicao}
                    onChange={(e) => alterar("pontoReposicao", e.target.value)}
                    placeholder="0,000000"
                  />
                  <Estilo.ExplicacaoCurta>
                    O saldo em que a reposição começa a agir.
                  </Estilo.ExplicacaoCurta>
                </Casca.LabelInputContainer>

                <Casca.LabelInputContainer>
                  <Casca.Label>Estoque de segurança</Casca.Label>
                  <Casca.Input
                    type="number"
                    min="0"
                    step="0.000001"
                    value={form.estoqueSeguranca}
                    onChange={(e) => alterar("estoqueSeguranca", e.target.value)}
                    placeholder="0,000000"
                  />
                </Casca.LabelInputContainer>
              </Casca.FormGrid>
            </Casca.FormSection>

            <Casca.FormSection>
              <Casca.SectionTitle>Quanto pedir</Casca.SectionTitle>
              <Estilo.GrupoDeRadio>
                <Estilo.OpcaoDeRadio>
                  <input
                    type="radio"
                    name="quantidadeModo"
                    checked={form.quantidadeModo === "fixa"}
                    onChange={() => alterar("quantidadeModo", "fixa")}
                  />
                  Quantidade fixa
                </Estilo.OpcaoDeRadio>
                <Estilo.OpcaoDeRadio>
                  <input
                    type="radio"
                    name="quantidadeModo"
                    checked={form.quantidadeModo === "ateOMaximo"}
                    onChange={() => alterar("quantidadeModo", "ateOMaximo")}
                  />
                  Completar até o máximo
                </Estilo.OpcaoDeRadio>
              </Estilo.GrupoDeRadio>

              <Casca.FormGrid>
                {form.quantidadeModo === "fixa" && (
                  <Casca.LabelInputContainer>
                    <Casca.Label>Quantidade fixa</Casca.Label>
                    <Casca.Input
                      type="number"
                      min="0"
                      step="0.000001"
                      value={form.quantidadeFixa}
                      onChange={(e) => alterar("quantidadeFixa", e.target.value)}
                      placeholder="0,000000"
                    />
                  </Casca.LabelInputContainer>
                )}

                {form.quantidadeModo === "ateOMaximo" && (
                  <Estilo.LinhaViva>
                    <div>
                      máximo ({form.estoqueMaximo || "—"}) − saldo (
                      {sugestao.saldo}) − em trânsito ({sugestao.emTransito})
                      {sugestao.embalagem !== null
                        ? `, arredondado para cima ao múltiplo de ${formatarQuantidade(
                            sugestao.embalagem
                          )}`
                        : ""}
                    </div>
                    <Estilo.ValorDaLinhaViva>
                      {sugestao.quantidade === null
                        ? "—"
                        : formatarQuantidade(sugestao.quantidade)}
                      <Casca.InfoBadge>calculado</Casca.InfoBadge>
                    </Estilo.ValorDaLinhaViva>
                    {/* A etiqueta não é enfeite: com saldo 0, o número acima é
                        o pedido MAIOR possível. Sem ela, ele passaria por
                        medição do estoque real — que não existe. */}
                    <Estilo.EtiquetaDeSuposicao>
                      <Casca.MaterialIcons>warning</Casca.MaterialIcons>
                      sem módulo de estoque, considera saldo 0
                    </Estilo.EtiquetaDeSuposicao>
                    <Estilo.ExplicacaoCurta>
                      Saldo e em trânsito dependem do módulo de movimento de
                      estoque, que ainda não existe. Enquanto não existir, a
                      conta assume zero nos dois.
                      {sugestao.embalagem === null
                        ? " Sem preço acordado vigente hoje, com embalagem, para este SKU neste fornecedor, não há múltiplo para arredondar."
                        : ""}
                    </Estilo.ExplicacaoCurta>
                  </Estilo.LinhaViva>
                )}
              </Casca.FormGrid>
            </Casca.FormSection>

            <Casca.FormSection>
              <Casca.SectionTitle>Que preço usar</Casca.SectionTitle>
              <Estilo.GrupoDeRadio>
                <Estilo.OpcaoDeRadio>
                  <input
                    type="radio"
                    name="precoModo"
                    checked={form.precoModo === "acordadoVigente"}
                    onChange={() => alterar("precoModo", "acordadoVigente")}
                  />
                  Preço acordado vigente
                </Estilo.OpcaoDeRadio>
                <Estilo.OpcaoDeRadio>
                  <input
                    type="radio"
                    name="precoModo"
                    checked={form.precoModo === "fixoTravado"}
                    onChange={() => alterar("precoModo", "fixoTravado")}
                  />
                  Preço fixo travado
                </Estilo.OpcaoDeRadio>
              </Estilo.GrupoDeRadio>

              {form.precoModo === "fixoTravado" && (
                <Casca.FormGrid>
                  <Casca.LabelInputContainer>
                    <Casca.Label>Preço fixo (R$)</Casca.Label>
                    {/* Dinheiro é Decimal(14,2): dois decimais, e só R$. */}
                    <Casca.Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.precoFixo}
                      onChange={(e) => alterar("precoFixo", e.target.value)}
                      placeholder="0,00"
                    />
                  </Casca.LabelInputContainer>
                </Casca.FormGrid>
              )}
              <Estilo.ExplicacaoCurta>
                "Vigente" segue a tabela de Preços Acordados do fornecedor na
                data do pedido. "Travado" ignora a tabela e usa o valor daqui.
              </Estilo.ExplicacaoCurta>
            </Casca.FormSection>

            <Casca.FormSection>
              <Casca.SectionTitle>Como agir</Casca.SectionTitle>
              <Estilo.GrupoDeRadio>
                <Estilo.OpcaoDeRadio>
                  <input
                    type="radio"
                    name="modo"
                    checked={form.modo === "sugerir"}
                    onChange={() => alterar("modo", "sugerir")}
                  />
                  Sugerir (padrão)
                </Estilo.OpcaoDeRadio>
                <Estilo.OpcaoDeRadio>
                  <input
                    type="radio"
                    name="modo"
                    checked={form.modo === "enviar"}
                    onChange={() => alterar("modo", "enviar")}
                  />
                  Enviar ao fornecedor
                </Estilo.OpcaoDeRadio>
              </Estilo.GrupoDeRadio>
              {form.modo === "enviar" && (
                <Casca.AlertMessage $tom="atencao">
                  Neste modo o pedido sai para o fornecedor sem ninguém conferir.
                  Um teto de linha baixo é o que impede um erro de digitação nas
                  quantidades de virar uma compra grande.
                </Casca.AlertMessage>
              )}

              <Casca.FormGrid style={{ marginTop: "var(--sp-16)" }}>
                <Casca.LabelInputContainer>
                  <Casca.Label>Teto por linha (R$)</Casca.Label>
                  <Casca.Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.tetoDeLinhaReais}
                    onChange={(e) => alterar("tetoDeLinhaReais", e.target.value)}
                    placeholder="0,00"
                  />
                  <Estilo.ExplicacaoCurta>
                    Valor máximo de uma linha do pedido, em reais.
                  </Estilo.ExplicacaoCurta>
                </Casca.LabelInputContainer>

                <Casca.LabelInputContainer>
                  <Casca.Label>Dispara das</Casca.Label>
                  <Casca.Input
                    type="time"
                    value={form.janelaDisparoInicio}
                    onChange={(e) =>
                      alterar("janelaDisparoInicio", e.target.value)
                    }
                  />
                </Casca.LabelInputContainer>

                <Casca.LabelInputContainer>
                  <Casca.Label>até</Casca.Label>
                  <Casca.Input
                    type="time"
                    value={form.janelaDisparoFim}
                    onChange={(e) => alterar("janelaDisparoFim", e.target.value)}
                  />
                </Casca.LabelInputContainer>
              </Casca.FormGrid>

              <Casca.Label>Dias em que dispara</Casca.Label>
              <Estilo.LinhaDeDias>
                {DIAS_DA_SEMANA.map((dia) => (
                  <Estilo.BotaoDeDia
                    key={dia.valor}
                    type="button"
                    ativo={form.janelaDisparoDias.indexOf(dia.valor) >= 0}
                    onClick={() => alternarDiaDeDisparo(dia.valor)}
                  >
                    {dia.rotulo}
                  </Estilo.BotaoDeDia>
                ))}
              </Estilo.LinhaDeDias>

              <Casca.FormGrid style={{ marginTop: "var(--sp-16)" }}>
                <Casca.LabelInputContainer>
                  <Casca.Label>Fornecedor</Casca.Label>
                  <Casca.Select
                    value={form.fornecedorUuid}
                    onChange={(e) => alterar("fornecedorUuid", e.target.value)}
                  >
                    <option value="">Selecione o fornecedor…</option>
                    {fornecedores.map((fornecedor: any) => (
                      <option key={fornecedor.uuid} value={fornecedor.uuid}>
                        {fornecedor.nome}
                      </option>
                    ))}
                  </Casca.Select>
                </Casca.LabelInputContainer>

                <Casca.LabelInputContainer>
                  <Casca.Label>Fornecedor alternativo</Casca.Label>
                  <Casca.Select
                    value={form.fornecedorAlternativoUuid}
                    onChange={(e) =>
                      alterar("fornecedorAlternativoUuid", e.target.value)
                    }
                  >
                    <option value="">Nenhum</option>
                    {fornecedores.map((fornecedor: any) => (
                      <option key={fornecedor.uuid} value={fornecedor.uuid}>
                        {fornecedor.nome}
                      </option>
                    ))}
                  </Casca.Select>
                </Casca.LabelInputContainer>

                <Casca.LabelInputContainer>
                  <Casca.Label>Escalar para o alternativo após (horas)</Casca.Label>
                  <Casca.Input
                    type="number"
                    min="0"
                    step="1"
                    value={form.prazoParaEscalarHoras}
                    onChange={(e) =>
                      alterar("prazoParaEscalarHoras", e.target.value)
                    }
                    placeholder="Ex.: 6"
                  />
                </Casca.LabelInputContainer>

                <Casca.LabelInputContainer>
                  <Casca.Label>Prazo de resposta (horas)</Casca.Label>
                  <Casca.Input
                    type="number"
                    min="0"
                    step="1"
                    value={form.prazoRespostaHoras}
                    onChange={(e) => alterar("prazoRespostaHoras", e.target.value)}
                    placeholder="Ex.: 2"
                  />
                </Casca.LabelInputContainer>

                <Casca.LabelInputContainer>
                  <Casca.Label>Política de lembrete</Casca.Label>
                  <Casca.Select
                    value={form.politicaLembrete}
                    onChange={(e) => alterar("politicaLembrete", e.target.value)}
                  >
                    {POLITICAS_DE_LEMBRETE.map((politica) => (
                      <option key={politica.valor} value={politica.valor}>
                        {politica.rotulo}
                      </option>
                    ))}
                  </Casca.Select>
                </Casca.LabelInputContainer>
              </Casca.FormGrid>
            </Casca.FormSection>

            <Casca.FormSection>
              <Casca.SectionTitle>Cobertura</Casca.SectionTitle>
              <Casca.FormGrid>
                <Casca.LabelInputContainer>
                  <Casca.Label>Cobertura alvo (dias)</Casca.Label>
                  <Casca.Input
                    type="number"
                    min="0"
                    step="1"
                    value={form.coberturaAlvoDias}
                    onChange={(e) => alterar("coberturaAlvoDias", e.target.value)}
                    placeholder="Ex.: 7"
                  />
                  <Estilo.ExplicacaoCurta>
                    Por quantos dias de consumo o pedido deve durar.
                  </Estilo.ExplicacaoCurta>
                </Casca.LabelInputContainer>

                <Casca.LabelInputContainer>
                  <Casca.Label>De onde veio esse número</Casca.Label>
                  <Estilo.GrupoDeRadio>
                    <Estilo.OpcaoDeRadio>
                      <input
                        type="radio"
                        name="coberturaAlvoOrigem"
                        checked={
                          form.coberturaAlvoOrigem === "sugeridoPeloFornecedor"
                        }
                        onChange={() =>
                          alterar(
                            "coberturaAlvoOrigem",
                            "sugeridoPeloFornecedor"
                          )
                        }
                      />
                      Sugerido pelo fornecedor
                    </Estilo.OpcaoDeRadio>
                    <Estilo.OpcaoDeRadio>
                      <input
                        type="radio"
                        name="coberturaAlvoOrigem"
                        checked={form.coberturaAlvoOrigem === "definidoPorVoce"}
                        onChange={() =>
                          alterar("coberturaAlvoOrigem", "definidoPorVoce")
                        }
                      />
                      Definido por você
                    </Estilo.OpcaoDeRadio>
                  </Estilo.GrupoDeRadio>
                </Casca.LabelInputContainer>

                {/* A regra de autoridade, escrita: sem ela o usuário supõe que
                    o fornecedor pode mudar o número dele depois. */}
                <Estilo.RegraDeAutoridade>
                  O Limite manda sempre. O fornecedor só sugere a cobertura no
                  momento em que este limite é criado; depois disso, o que vale
                  é o que está gravado aqui — nenhuma sugestão dele altera este
                  campo sozinha.
                </Estilo.RegraDeAutoridade>
              </Casca.FormGrid>
            </Casca.FormSection>

            <Casca.FormSection>
              <Casca.SectionTitle>Criticidade</Casca.SectionTitle>
              <Casca.CheckboxContainer>
                <Casca.CheckboxInput>
                  <input
                    type="checkbox"
                    checked={form.critico}
                    onChange={(e) => alterar("critico", e.target.checked)}
                  />
                  <span className="checkmark" />
                </Casca.CheckboxInput>
                <Casca.CheckboxLabel active={form.critico}>
                  Crítico / urgente
                </Casca.CheckboxLabel>
              </Casca.CheckboxContainer>

              <Casca.FormGrid>
                <Casca.LabelInputContainer>
                  <Casca.Label>Rearme (horas)</Casca.Label>
                  <Casca.Input
                    type="number"
                    min="0"
                    step="1"
                    value={form.rearmeHoras}
                    onChange={(e) => alterar("rearmeHoras", e.target.value)}
                    placeholder="Ex.: 24"
                  />
                  <Estilo.ExplicacaoCurta>
                    Quanto esperar antes de este limite poder disparar de novo.
                  </Estilo.ExplicacaoCurta>
                </Casca.LabelInputContainer>

                <Casca.LabelInputContainer>
                  {/* Dois campos separados de propósito: horas e % nunca no
                      mesmo campo. Misturar unidade na mesma coluna é o defeito
                      que já existe em produção. */}
                  <Casca.Label>Rearme se cair abaixo de (%)</Casca.Label>
                  <Casca.Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.rearmePercentualAbaixo}
                    onChange={(e) =>
                      alterar("rearmePercentualAbaixo", e.target.value)
                    }
                    placeholder="Ex.: 20"
                  />
                  <Estilo.ExplicacaoCurta>
                    Percentual do ponto de reposição. Rearma antes das horas
                    acabarem se o saldo despencar.
                  </Estilo.ExplicacaoCurta>
                </Casca.LabelInputContainer>

                <Casca.LabelInputContainer>
                  <Casca.Label>Tipo de alerta</Casca.Label>
                  <Casca.Select
                    value={form.alertType}
                    onChange={(e) => alterar("alertType", e.target.value)}
                  >
                    {TIPOS_DE_ALERTA.map((tipo) => (
                      <option key={tipo.valor} value={tipo.valor}>
                        {tipo.rotulo}
                      </option>
                    ))}
                  </Casca.Select>
                </Casca.LabelInputContainer>
              </Casca.FormGrid>
            </Casca.FormSection>
          </Casca.FormContainer>
        </Casca.ModalBody>

        <Casca.ModalFooter>
          {/* O que o Salvar faz, dito antes de ser apertado. */}
          <Estilo.NotaDeCampo>
            Salvar envia esta regra em{" "}
            <code>
              {limiteUuid
                ? "PATCH suprimentos/limites-de-reposicao/:uuid"
                : "POST suprimentos/limites-de-reposicao"}
            </code>
            . O que está gravado aqui decide quando pedir, quanto pedir e por
            quanto — a quantidade sugerida acima ainda supõe saldo 0.
          </Estilo.NotaDeCampo>

          <Casca.ButtonGroup>
            <Casca.SecondaryButton type="button" onClick={handleCloseModal}>
              <Casca.MaterialIcons>close</Casca.MaterialIcons>
              Cancelar
            </Casca.SecondaryButton>
            <Casca.PrimaryButton
              type="button"
              onClick={handleSubmit}
              disabled={salvando}
            >
              <Casca.MaterialIcons>save</Casca.MaterialIcons>
              {salvando
                ? "Salvando…"
                : limiteUuid
                ? "Atualizar Limite"
                : "Criar Limite"}
            </Casca.PrimaryButton>
          </Casca.ButtonGroup>

          {aviso && (
            <Casca.AlertMessage $tom={aviso.tom}>
              {aviso.texto}
            </Casca.AlertMessage>
          )}
        </Casca.ModalFooter>
      </Casca.ModalContent>
    </Casca.ModalOverlay>,
    document.body
  );
};

export default LimitModal;
